import type { IconName } from '@kiips/ui';
import type { Journey } from '@my-fullstack-app/itinerary-types';
import { wallTimeToUtcMs } from './timezone';

/**
 * Pull 'HH:mm' from an ISO datetime ('2026-10-21T23:00') or pass a
 * 'HH:mm' through unchanged. Returns '—' on garbage input rather than
 * throwing, so the timeline never crashes on a bad journey time.
 */
export function isoTimeToHHmm(iso: string): string {
  if (!iso) return '—';
  if (/^\d{2}:\d{2}$/.test(iso)) return iso;
  const idx = iso.indexOf('T');
  if (idx < 0) return '—';
  const hhmm = iso.slice(idx + 1, idx + 6);
  return /^\d{2}:\d{2}$/.test(hhmm) ? hhmm : '—';
}

/**
 * Number of midnight-rollovers between origin and destination — used to
 * show a small "+1d" badge on overnight journeys (e.g. JFK 23:00 → CDG
 * 11:00 next day). Returns 0 when same-date or when either side is
 * malformed.
 */
export function dayRolloverCount(j: Journey): number {
  const from = j.origin.time;
  const to = j.destination.time;
  if (!from || !to) return 0;
  const fromDate = from.split('T')[0];
  const toDate = to.split('T')[0];
  if (!fromDate || !toDate || fromDate === toDate) return 0;
  const a = new Date(fromDate + 'T00:00:00Z').getTime();
  const b = new Date(toDate + 'T00:00:00Z').getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

/**
 * Compute the journey's true elapsed duration in minutes.
 *
 * When both anchors carry a timezone, the wall-clock times are
 * converted to UTC using DST-aware Intl arithmetic — so a JFK 23:00
 * → CDG 11:00 flight reads as 6h, not 12h.
 *
 * When either timezone is missing, the function falls back to the
 * naive wall-clock delta (treating both ends as floating-time in the
 * same implicit zone). This is wrong for cross-tz flights but matches
 * the rest of the app's pre-tz behaviour and is a safe legacy default.
 *
 * Returns `null` when:
 *   - either endpoint is missing,
 *   - the times can't be parsed,
 *   - destination is not strictly after origin (form should already
 *     guard this, but a stale entity might violate it).
 */
export function computeJourneyDurationMinutes(j: Journey): number | null {
  const o = j.origin.time;
  const d = j.destination.time;
  if (!o || !d) return null;
  const oTz = j.origin.timezone;
  const dTz = j.destination.timezone;
  let oMs: number | null;
  let dMs: number | null;
  if (oTz && dTz) {
    oMs = wallTimeToUtcMs(o, oTz);
    dMs = wallTimeToUtcMs(d, dTz);
  } else {
    // Both treated as naive UTC so the diff is symmetric — same result
    // as `Date.parse(o + 'Z') - Date.parse(d + 'Z')`.
    oMs = parseFloatingAsUtc(o);
    dMs = parseFloatingAsUtc(d);
  }
  if (oMs == null || dMs == null) return null;
  const diff = dMs - oMs;
  if (diff <= 0) return null;
  return Math.round(diff / 60_000);
}

function parseFloatingAsUtc(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(iso);
  if (!m) return null;
  const ms = Date.UTC(
    Number(m[1]), Number(m[2]) - 1, Number(m[3]),
    Number(m[4]), Number(m[5]), Number(m[6] ?? '0'),
  );
  return Number.isNaN(ms) ? null : ms;
}

/** Pretty-print "8h 30m" / "45m" / "1h" for a positive minute count. */
export function formatDurationShort(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Human label for a Journey mode. */
export function journeyModeLabel(mode: Journey['mode']): string {
  switch (mode) {
    case 'flight': return 'Flight';
    case 'train': return 'Train';
    case 'bus': return 'Bus';
    case 'ferry': return 'Ferry';
    case 'car-rental': return 'Car';
    case 'transfer': return 'Transfer';
  }
}

/** Material icon name for a Journey mode. */
export function journeyModeIcon(mode: Journey['mode']): IconName {
  switch (mode) {
    case 'flight': return 'flight';
    case 'train': return 'train';
    case 'bus': return 'directions-bus';
    case 'ferry': return 'directions-boat';
    case 'car-rental': return 'directions-car';
    case 'transfer': return 'directions-car';
  }
}

/** Material icon for a Journey anchor — distinct icons for the depart
 *  vs arrive cards (ADR-0049). Flights use the airline-standard
 *  takeoff/land pair; other modes keep the mode icon on departure and
 *  use a generic location pin for arrival. */
export function journeyAnchorIcon(
  mode: Journey['mode'],
  anchor: 'origin' | 'destination',
): string {
  if (anchor === 'origin') {
    return mode === 'flight' ? 'flight-takeoff' : journeyModeIcon(mode);
  }
  if (mode === 'flight') return 'flight-land';
  return 'place';
}

/**
 * Most airport / station Google Places results carry an IATA-like code
 * in trailing parens, e.g. "John F. Kennedy International Airport (JFK)"
 * or "Charles de Gaulle Airport (CDG)". Pull the 3-letter all-caps token
 * if present, otherwise return undefined so the caller can leave the
 * existing user-entered code alone.
 */
export function extractTransitCode(name: string): string | undefined {
  const m = name.match(/\(([A-Z]{3})\)/);
  return m?.[1];
}
